// SelectInput.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import Select, { components } from "react-select";
import Skeleton from "@mui/material/Skeleton";
import FormControl from "@mui/material/FormControl";

const DEFAULT_Z = 9999;
const GAP = 8;
const MIN_VIABLE = 220;
const DEFAULT_MAX = 300;

const getScrollParents = (node) => {
  const parents = [];
  let cur = node && node.parentElement;
  while (cur) {
    const style = getComputedStyle(cur);
    const overflowY = `${style.overflow}${style.overflowY}${style.overflowX}`.toLowerCase();
    if (/auto|scroll|overlay/.test(overflowY)) parents.push(cur);
    cur = cur.parentElement;
  }
  parents.push(window);
  return parents;
};

const computePlacementAndRect = (anchorEl) => {
  if (!anchorEl) return { mode: "down", rect: null };
  const r = anchorEl.getBoundingClientRect();
  const vh = window.innerHeight;
  const spaceBelow = vh - r.bottom - GAP;
  const spaceAbove = r.top - GAP;

  if (spaceBelow >= MIN_VIABLE) return { mode: "down", rect: r };
  if (spaceAbove >= MIN_VIABLE) return { mode: "up", rect: r };
  return { mode: "fullscreen", rect: r };
};

const SelectInput = ({
  value,
  onChange,
  options = [],
  placeholder = "Select",
  maxWidth = "none",
  name,
  error = false,
  loading = false,
  dropdownMaxHeight = DEFAULT_MAX,
  customStyle = {},
  isDisabled = false,
  menuIsOpen: controlledMenuIsOpen,
  onMenuOpen,
  onMenuClose,
  zIndex = DEFAULT_Z,
  usePortal = true,
  portalTargetEl,
  lockBodyWhenFullscreen = true,
  menuPlacement: userMenuPlacement = "auto",
}) => {
  const anchorRef = useRef(null);
  const roRef = useRef(null);
  const scrollParentsRef = useRef([]);

  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState("down");
  const [rect, setRect] = useState(null);

  const selectedOption = useMemo(
    () => options.find((opt) => opt.value == value) || null,
    [options, value]
  );

  const handleChange = (selected) => {
    onChange?.({
      target: { name, value: selected ? selected.value : "" },
    });
  };

  const recalc = () => {
    if (!anchorRef.current) return;
    const { mode: m, rect: r } = computePlacementAndRect(anchorRef.current);
    setMode(m);
    setRect(r);
  };

  const attachScrollListeners = () => {
    if (!anchorRef.current) return;
    const parents = getScrollParents(anchorRef.current);
    scrollParentsRef.current = parents;
    parents.forEach((p) => {
      const opts = { passive: true };
      p.addEventListener("scroll", recalc, opts);
      p.addEventListener("resize", recalc, opts);
    });
  };

  const detachScrollListeners = () => {
    scrollParentsRef.current.forEach((p) => {
      try {
        p.removeEventListener("scroll", recalc);
        p.removeEventListener("resize", recalc);
      } catch {}
    });
    scrollParentsRef.current = [];
  };

  const attachResizeObserver = () => {
    if (!("ResizeObserver" in window) || !anchorRef.current) return;
    roRef.current = new ResizeObserver(recalc);
    roRef.current.observe(anchorRef.current);
  };
  const detachResizeObserver = () => {
    if (roRef.current) {
      try {
        roRef.current.disconnect();
      } catch {}
      roRef.current = null;
    }
  };

  const lockBody = () => {
    if (!lockBodyWhenFullscreen) return;
    document.body.style.overflow = "hidden";
  };
  const unlockBody = () => {
    if (!lockBodyWhenFullscreen) return;
    document.body.style.overflow = "";
  };

  const openMenu = () => {
    const { mode: m, rect: r } = computePlacementAndRect(anchorRef.current);
    setMode(m);
    setRect(r);
    setIsOpen(true);
    attachScrollListeners();
    attachResizeObserver();
    if (m === "fullscreen") lockBody();
    onMenuOpen?.();
  };

  const closeMenu = () => {
    setIsOpen(false);
    detachScrollListeners();
    detachResizeObserver();
    unlockBody();
    onMenuClose?.();
  };

  useEffect(() => {
    return () => {
      detachScrollListeners();
      detachResizeObserver();
      unlockBody();
    };
  }, []);

  const styles = {
    control: (base, state) => ({
      ...base,
      borderRadius: 4,
      height: 40,
      minHeight: 40,
      maxWidth,
      borderColor: error ? "red" : state.isFocused ? "#aac7e7" : "#C0C0C0",
      boxShadow: state.isFocused ? "0 0 0 1px #aac7e7" : "none",
      fontFamily: "Geist, sans-serif",
      fontSize: 16,
      background: customStyle?.background ?? base.background,
      ...customStyle,
    }),
    placeholder: (base) => ({ ...base, color: "#999" }),
    singleValue: (base) => ({
      ...base,
      color: customStyle?.isWhiteText ? "#fff" : "#000",
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex,
      position: "fixed",
      top: 0,
      left: 0,
      width: "100vw",
      height: "100vh",
      pointerEvents: "none",
    }),
    menu: (base) => {
      if (!rect) {
        return {
          ...base,
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          maxHeight: dropdownMaxHeight,
          pointerEvents: "auto",
        };
      }

      const vh = window.innerHeight;
      const spaceBelow = vh - rect.bottom - GAP;
      const spaceAbove = rect.top - GAP;

      if (mode === "fullscreen") {
        return {
          ...base,
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: "100vw",
          maxHeight: "100vh",
          margin: 0,
          borderRadius: 0,
          pointerEvents: "auto",
        };
      }

      if (mode === "down") {
        return {
          ...base,
          position: "fixed",
          top: rect.bottom + GAP,
          left: rect.left,
          width: rect.width,
          maxHeight: Math.max(100, Math.min(dropdownMaxHeight, spaceBelow)),
          pointerEvents: "auto",
        };
      }

      const maxH = Math.max(100, Math.min(dropdownMaxHeight, spaceAbove));
      const topPos = Math.max(GAP, rect.top - maxH);
      return {
        ...base,
        position: "fixed",
        top: topPos,
        left: rect.left,
        width: rect.width,
        maxHeight: maxH,
        pointerEvents: "auto",
      };
    },
    menuList: (base) => ({
      ...base,
      maxHeight: mode === "fullscreen" ? "100vh" : base.maxHeight ?? dropdownMaxHeight,
      overflowY: "auto",
    }),
  };

  const Menu = (props) => {
    if (mode !== "fullscreen") return <components.Menu {...props} />;
    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          display: "flex",
          flexDirection: "column",
          zIndex,
          background: "#fff",
        }}
      >
        <div style={{ flex: "1 1 auto", overflowY: "auto" }}>{props.children}</div>
      </div>
    );
  };

  if (loading) {
    return (
      <FormControl fullWidth size="small">
        <Skeleton variant="rectangular" height={40} width="100%" sx={{ borderRadius: 1 }} />
      </FormControl>
    );
  }

  return (
    <FormControl fullWidth size="small" ref={anchorRef}>
      <Select
        isDisabled={isDisabled}
        options={options}
        value={selectedOption}
        onChange={handleChange}
        placeholder={placeholder}
        isClearable
        styles={styles}
        menuPortalTarget={usePortal ? (portalTargetEl || document.body) : undefined}
        menuPosition={usePortal ? "fixed" : "absolute"}
        menuPlacement={userMenuPlacement}
        menuShouldScrollIntoView={false} // prevent auto-scrolling
        onMenuOpen={openMenu}
        onMenuClose={closeMenu}
        menuIsOpen={controlledMenuIsOpen}
        name={name}
        components={{ Menu }}
      />
    </FormControl>
  );
};

export default SelectInput;
